import { useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  useUpdateBugDetailMutation,
  useUpdateBugIndexMutation,
  useUpdateStatusIndexMutation,
} from "../../../../features/bughurd/api";
import {
  setIsDragging,
  setColumns,
  setColumnOrder,
  setPagination,
  setHasMore,
  setSubGroupData,
} from "@/features/bughurd/slice";
import {
  selectColumns,
  selectColumnOrder,
  selectGroupingConfig,
  selectGroupingDataMemoized,
  selectBoardState,
} from "@/features/bughurd/boardSelector";
import { useBoardData } from "./useBoardData";
import { useBoardFetch } from "./useBoardFetch";

export const useDragAndDrop = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(["bugherd"]);
  const dispatch = useAppDispatch();

  const columns = useAppSelector(selectColumns);
  const columnOrder = useAppSelector(selectColumnOrder);
  const groupingConfig = useAppSelector(selectGroupingConfig);
  const groupingData = useAppSelector(selectGroupingDataMemoized);
  const boardState = useAppSelector(selectBoardState);

  const { viewType, subGroupData } = boardState;
  const { groupBy, subGroupBy } = groupingConfig;

  const [updateBug] = useUpdateBugDetailMutation();
  const [updateBugIndex] = useUpdateBugIndexMutation();
  const [updateStatusIndex] = useUpdateStatusIndexMutation();

  const {
    dragOperationInProgress,
    isDragInProgress,
    skipStatusRefetch,
    PAGE_LIMIT,
    preserveScrollPosition,
    restoreScrollPosition,
    previousTasksRef,
  } = useBoardData();

  const { fetchBugsForGroup } = useBoardFetch();

  // Helper function to sort tasks by boardIndex
  const sortTasksByBoardIndex = useCallback((tasks: any[]) => {
    return [...tasks].sort((a, b) => {
      const aIndex = a.boardIndex ?? Number.MAX_SAFE_INTEGER;
      const bIndex = b.boardIndex ?? Number.MAX_SAFE_INTEGER;

      if (aIndex !== bIndex) {
        return aIndex - bIndex;
      }

      const aDate = new Date(a.createdAt || 0).getTime();
      const bDate = new Date(b.createdAt || 0).getTime();
      return bDate - aDate;
    });
  }, []);

  const handleDragStart = useCallback(() => {
    dispatch(setIsDragging(true));
    dragOperationInProgress.current = true;
    isDragInProgress.current = true;
    skipStatusRefetch.current = true;

    const startEvent = new Event("__rbd-drag-start");
    window.dispatchEvent(startEvent);
  }, [dispatch, dragOperationInProgress, isDragInProgress, skipStatusRefetch]);

  const handleDragEnd = useCallback(
    async (result: any) => {
      dispatch(setIsDragging(false));

      const endEvent = new Event("__rbd-drag-end");
      window.dispatchEvent(endEvent);

      const { destination, source, draggableId, type } = result;

      if (
        !destination ||
        (destination.droppableId === source.droppableId &&
          destination.index === source.index)
      ) {
        dragOperationInProgress.current = false;
        isDragInProgress.current = false;
        skipStatusRefetch.current = false;
        return;
      }

      if (subGroupBy !== "None" && type === "COLUMN") {
        toast.error("Column reordering is not available in sub-grouped view");
        dragOperationInProgress.current = false;
        isDragInProgress.current = false;
        skipStatusRefetch.current = false;
        return;
      }

      if (viewType !== "kanban") {
        dragOperationInProgress.current = false;
        isDragInProgress.current = false;
        skipStatusRefetch.current = false;
        return;
      }

      // Helper function to normalize mixed array (objects/IDs) to IDs only
      const normalizeToIds = (arr: any[]): string[] => {
        if (!Array.isArray(arr)) return [];
        return arr
          .map((item) =>
            typeof item === "object" && item?._id ? item._id : item
          )
          .filter(Boolean);
      };

      // Handle task drag and drop (works for both regular and subgroup views)
      const isSubGroupView = subGroupBy !== "None";
      let sourceColumn, destColumn;

      if (isSubGroupView) {
        // Parse subgroup droppableId format: "subGroupId-columnId"
        const [sourceSubGroupId, sourceColumnId] =
          source.droppableId.split("-");
        const [destSubGroupId, destColumnId] =
          destination.droppableId.split("-");

        sourceColumn = subGroupData[sourceSubGroupId]?.columns[sourceColumnId];
        destColumn = subGroupData[destSubGroupId]?.columns[destColumnId];
      } else {
        sourceColumn = columns[source.droppableId];
        destColumn = columns[destination.droppableId];
      }

      if (!sourceColumn || !destColumn) {
        console.error("Source or destination column not found");
        dragOperationInProgress.current = false;
        isDragInProgress.current = false;
        skipStatusRefetch.current = false;
        return;
      }

      const sourceTasks = [...sourceColumn.tasks];
      const destTasks =
        source.droppableId === destination.droppableId
          ? sourceTasks
          : [...destColumn.tasks];

      const taskIndex = sourceTasks.findIndex(
        (task) => task._id === draggableId || task.id === draggableId
      );

      if (taskIndex === -1) {
        console.error("Task not found in source column:", draggableId);
        dragOperationInProgress.current = false;
        isDragInProgress.current = false;
        skipStatusRefetch.current = false;
        return;
      }

      const [removed] = sourceTasks.splice(taskIndex, 1);
      destTasks?.splice(destination.index, 0, removed);

      let updatedTask = {
        ...removed,
        project: removed.project?._id || id,
        assignTo: normalizeToIds(removed?.assignTo || []),
      };

      let updatePayload = null;

      // Handle cross-column drag
      if (source.droppableId !== destination.droppableId) {
        const destGroupData = isSubGroupView
          ? groupingData[destination.droppableId.split("-")[1]]
          : groupingData[destination.droppableId];

        // Base payload with bug status preservation
        updatePayload = {
          ...removed,
          bugId: removed?._id,
          boardIndex: destination.index, // Add boardIndex to payload
          // Preserve bug status when moving between non-status columns
          ...(removed?.bugStatus?._id &&
            destGroupData.type !== "status" && {
              bugStatus: removed?.bugStatus?._id,
            }),
          project: removed.project?._id || id,
          assignTo: normalizeToIds(removed?.assignTo || []),
        };

        // Update the task with new boardIndex
        updatedTask = {
          ...updatedTask,
          boardIndex: destination.index,
        };

        // Combined switch case for both optimistic updates and API payload
        switch (destGroupData.type) {
          case "status":
            // Optimistic update
            updatedTask = {
              ...updatedTask,
              bugStatus:
                typeof destGroupData.statusData === "object"
                  ? destGroupData.statusData
                  : {
                      _id: isSubGroupView
                        ? destination.droppableId.split("-")[1]
                        : destination.droppableId,
                      ...destGroupData,
                    },
            };
            // API payload
            updatePayload.bugStatus = isSubGroupView
              ? destination.droppableId.split("-")[1]
              : destination.droppableId;
            break;

          case "assignee":
            // Enhanced assignee logic for multiple assignees
            let currentAssignees = removed.assignTo || [];

            const normalizedCurrentAssignees = normalizeToIds(currentAssignees);

            const sourceAssigneeId = isSubGroupView
              ? source.droppableId.split("-")[1]
              : source.droppableId;
            const destAssigneeId = isSubGroupView
              ? destination.droppableId.split("-")[1]
              : destination.droppableId;

            const filteredAssigneeIds = normalizedCurrentAssignees.filter(
              (assigneeId) => assigneeId !== sourceAssigneeId
            );

            if (!filteredAssigneeIds.includes(destAssigneeId)) {
              filteredAssigneeIds.push(destAssigneeId);
            }

            // Optimistic update with full objects
            const updatedAssignees = filteredAssigneeIds.map((assigneeId) => {
              const existingAssignee = currentAssignees.find(
                (assignee) =>
                  (typeof assignee === "object" ? assignee._id : assignee) ===
                  assigneeId
              );

              if (existingAssignee && typeof existingAssignee === "object") {
                return existingAssignee;
              }

              if (assigneeId === destAssigneeId) {
                return {
                  _id: destAssigneeId,
                  fullName:
                    destGroupData.title ||
                    destGroupData.fullName ||
                    "Unknown User",
                };
              }

              return (
                existingAssignee || {
                  _id: assigneeId,
                  fullName: "Unknown User",
                }
              );
            });

            updatedTask = {
              ...updatedTask,
              assignTo: updatedAssignees,
            };

            // API payload - only IDs
            updatePayload.assignTo = filteredAssigneeIds;
            break;

          case "priority":
            // Optimistic update
            updatedTask = {
              ...updatedTask,
              severity: destGroupData.title,
            };
            // API payload
            updatePayload.severity = destGroupData.title;
            break;

          case "tag":
            // Optimistic update
            updatedTask = {
              ...updatedTask,
              tags: [destGroupData.tagName || destGroupData.title],
            };
            // API payload
            updatePayload.tags = [destGroupData.tagName || destGroupData.title];
            break;

          case "taskType":
            // Optimistic update
            updatedTask = {
              ...updatedTask,
              type: destGroupData.title,
            };
            // API payload
            updatePayload.type = destGroupData.title;
            break;

          default:
            break;
        }

        // Update destTasks with the optimistically updated task
        destTasks[destination.index] = updatedTask;
      } else {
        // Same column reordering - update boardIndex
        updatedTask = {
          ...updatedTask,
          boardIndex: destination.index,
        };
        destTasks[destination.index] = updatedTask;
      }

      // Preserve scroll position
      if (isSubGroupView) {
        preserveScrollPosition(source.droppableId);
        if (source.droppableId !== destination.droppableId) {
          preserveScrollPosition(destination.droppableId);
        }
      } else {
        preserveScrollPosition(source.droppableId);
        if (source.droppableId !== destination.droppableId) {
          preserveScrollPosition(destination.droppableId);
        }
      }

      // OPTIMISTIC UPDATE: Update Redux state immediately with proper boardIndex
      if (isSubGroupView) {
        // Update subgroup data
        const [sourceSubGroupId, sourceColumnId] =
          source.droppableId.split("-");
        const [destSubGroupId, destColumnId] =
          destination.droppableId.split("-");

        // Create optimistic update for subgroup data
        const optimisticSubGroupData = { ...subGroupData };

        // Update tasks with proper boardIndex and sort them
        const updatedSourceTasks = sourceTasks.map((task, index) => ({
          ...task,
          boardIndex: index,
        }));

        const updatedDestTasks = destTasks.map((task, index) => ({
          ...task,
          boardIndex: index,
        }));

        // Sort the updated tasks to maintain order
        optimisticSubGroupData[sourceSubGroupId] = {
          ...optimisticSubGroupData[sourceSubGroupId],
          columns: {
            ...optimisticSubGroupData[sourceSubGroupId].columns,
            [sourceColumnId]: {
              ...optimisticSubGroupData[sourceSubGroupId].columns[
                sourceColumnId
              ],
              tasks: sortTasksByBoardIndex(updatedSourceTasks),
              totalCount:
                source.droppableId !== destination.droppableId
                  ? Math.max((sourceColumn.totalCount || 0) - 1, 0)
                  : sourceColumn.totalCount,
            },
          },
        };

        // Update destination subgroup and column (only if different from source)
        if (
          sourceSubGroupId !== destSubGroupId ||
          sourceColumnId !== destColumnId
        ) {
          optimisticSubGroupData[destSubGroupId] = {
            ...optimisticSubGroupData[destSubGroupId],
            columns: {
              ...optimisticSubGroupData[destSubGroupId].columns,
              [destColumnId]: {
                ...optimisticSubGroupData[destSubGroupId].columns[destColumnId],
                tasks: sortTasksByBoardIndex(updatedDestTasks),
                totalCount:
                  source.droppableId !== destination.droppableId
                    ? (destColumn.totalCount || 0) + 1
                    : destColumn.totalCount,
              },
            },
          };
        }

        dispatch(setSubGroupData(optimisticSubGroupData));
      } else {
        // Update regular columns with proper task ordering
        const updatedSourceTasks = sourceTasks.map((task, index) => ({
          ...task,
          boardIndex: index,
        }));

        const updatedDestTasks = destTasks.map((task, index) => ({
          ...task,
          boardIndex: index,
        }));

        const optimisticColumns = {
          ...columns,
          [source.droppableId]: {
            ...sourceColumn,
            tasks: sourceTasks,
            totalCount:
              source.droppableId !== destination.droppableId
                ? (sourceColumn.totalCount || 1) - 1
                : sourceColumn.totalCount,
          },
          [destination.droppableId]: {
            ...destColumn,
            tasks: destTasks,
            totalCount:
              source.droppableId !== destination.droppableId
                ? (destColumn.totalCount || 0) + 1
                : destColumn.totalCount,
          },
        };

        dispatch(setColumns(optimisticColumns));
      }

      try {
        if (updatePayload) {
          delete updatePayload?.createdBy;
          delete updatePayload?.createdAt;
          delete updatePayload?.updatedAt;
          updatePayload.currentSprint = updatePayload?.currentSprint?._id;
          await updateBug(updatePayload).unwrap();
        }
        const movedId = draggableId;
        const prevId = destTasks[destination.index - 1]?._id || null;
        const nextId = destTasks[destination.index + 1]?._id || null;

        const indexPayload = {
          lexo: true,
          project: removed.project?._id || id,
          movedId: movedId,
          prevId: prevId,
          nextId: nextId,
        };

        await updateBugIndex(indexPayload).unwrap();

        toast.success("Task moved successfully");
        if (source.droppableId !== destination.droppableId) {
          // Cross-column move
          //   const updatedItemsPayload = {
          //     // items: [
          //     //   {
          //     //     _id: removed._id,
          //     //     boardIndex: destination.index,
          //     //   },
          //     // ],
          //     crossColumn: "true",
          //     items: {
          //       _id: removed._id,
          //       boardIndex: destination.index,
          //       bugStatus: destination.droppableId,
          //       project: removed.project._id,
          //     },
          //   };

          //   await updateBug(updatePayload);
          //   await updateBugIndex(updatedItemsPayload);

          // toast.success("Task moved successfully");

          // Reset pagination and refresh data
          if (isSubGroupView) {
            // Reset pagination for subgroup columns
            dispatch(
              setPagination({
                key: source.droppableId,
                pagination: {
                  page: 1,
                  limit: PAGE_LIMIT,
                },
              })
            );

            dispatch(
              setPagination({
                key: destination.droppableId,
                pagination: {
                  page: 1,
                  limit: PAGE_LIMIT,
                },
              })
            );

            // Update hasMore status
            dispatch(
              setHasMore({
                key: source.droppableId,
                hasMore: true,
              })
            );

            dispatch(
              setHasMore({
                key: destination.droppableId,
                hasMore: true,
              })
            );

            previousTasksRef.current[source.droppableId] = 0;
            previousTasksRef.current[destination.droppableId] = 0;
          } else {
            // Regular column updates
            dispatch(
              setPagination({
                key: source.droppableId,
                pagination: {
                  page: 1,
                  limit: PAGE_LIMIT,
                },
              })
            );

            dispatch(
              setPagination({
                key: destination.droppableId,
                pagination: {
                  page: 1,
                  limit: PAGE_LIMIT,
                },
              })
            );

            dispatch(
              setHasMore({
                key: source.droppableId,
                hasMore: true,
              })
            );

            dispatch(
              setHasMore({
                key: destination.droppableId,
                hasMore: true,
              })
            );

            previousTasksRef.current[source.droppableId] = 0;
            previousTasksRef.current[destination.droppableId] = 0;
          }

          isDragInProgress.current = false;
          skipStatusRefetch.current = false;
        } else {
          // Same column reordering
          // const reorderItemsPayload = {
          //   items: destTasks.map((task, index) => ({
          //     _id: task._id,
          //     boardIndex: index,
          //   })),
          // };

          // await updateBugIndex(reorderItemsPayload);

          // toast.success("Task reordered successfully");

          if (isSubGroupView) {
            const [subGroupId, columnId] = source.droppableId.split("-");

            const columnGroupData = groupingData[columnId];

            // Keep the optimistic update but don't reset pagination
            // This maintains the current view while keeping the new order

            previousTasksRef.current[source.droppableId] = destTasks.length;

            isDragInProgress.current = false;
            skipStatusRefetch.current = false;

            // Don't refetch data immediately, let the optimistic update stay
            // Only restore scroll position
            setTimeout(() => {
              restoreScrollPosition(source.droppableId);
            }, 100);
          } else {
            // Regular column reordering logic
            const columnGroupData = groupingData[source.droppableId];

            // Keep the optimistic update but don't reset pagination
            // This maintains the current view while keeping the new order

            previousTasksRef.current[source.droppableId] = destTasks.length;

            isDragInProgress.current = false;
            skipStatusRefetch.current = false;

            // Don't refetch data immediately, let the optimistic update stay
            // Only restore scroll position
            setTimeout(() => {
              restoreScrollPosition(source.droppableId);
            }, 100);
          }
        }
      } catch (error: any) {
        console.error("Drag operation failed:", error);
        toast.error(error?.data?.message || "Failed to move task");

        // Revert the UI to its original state on error
        if (isSubGroupView) {
          dispatch(setSubGroupData(subGroupData));
        } else {
          dispatch(setColumns(columns));
        }

        // Reset pagination and refresh data on error
        const columnsToRefresh =
          source.droppableId !== destination.droppableId
            ? [source.droppableId, destination.droppableId]
            : [source.droppableId];

        for (const columnId of columnsToRefresh) {
          dispatch(
            setPagination({
              key: columnId,
              pagination: {
                page: 1,
                limit: PAGE_LIMIT,
              },
            })
          );

          dispatch(
            setHasMore({
              key: columnId,
              hasMore: true,
            })
          );

          previousTasksRef.current[columnId] = 0;
        }

        // Refresh affected columns
        if (isSubGroupView) {
          for (const columnId of columnsToRefresh) {
            const [subGroupId, actualColumnId] = columnId.split("-");
            const columnGroupData = groupingData[actualColumnId];
            if (columnGroupData) {
              await fetchBugsForGroup(
                actualColumnId,
                columnGroupData,
                1,
                PAGE_LIMIT,
                false,
                true,
                subGroupId
              );
            }
          }
        } else {
          for (const columnId of columnsToRefresh) {
            const columnGroupData = groupingData[columnId];
            if (columnGroupData) {
              await fetchBugsForGroup(
                columnId,
                columnGroupData,
                1,
                PAGE_LIMIT,
                false,
                true
              );
            }
          }
        }
      } finally {
        dragOperationInProgress.current = false;
        isDragInProgress.current = false;
        skipStatusRefetch.current = false;
      }
    },
    [
      dispatch,
      subGroupBy,
      columnOrder,
      groupBy,
      updateStatusIndex,
      t,
      columns,
      subGroupData,
      preserveScrollPosition,
      groupingData,
      updateBug,
      updateBugIndex,
      viewType,
      PAGE_LIMIT,
      previousTasksRef,
      isDragInProgress,
      skipStatusRefetch,
      fetchBugsForGroup,
      restoreScrollPosition,
      dragOperationInProgress,
      id,
      sortTasksByBoardIndex,
    ]
  );

  return {
    handleDragStart,
    handleDragEnd,
  };
};

export const useColumnDragAndDrop = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(["bugherd"]);
  const dispatch = useAppDispatch();

  const columnOrder = useAppSelector(selectColumnOrder);
  const groupingConfig = useAppSelector(selectGroupingConfig);
  const { groupBy, subGroupBy } = groupingConfig;

  const [updateStatusIndex] = useUpdateStatusIndexMutation();

  const handleColumnDragEnd = useCallback(
    async (activeId: string, overId: string) => {
      // Don't allow drag and drop when sub-grouping is enabled
      if (subGroupBy !== "None") {
        toast.error("Column reordering is not available in sub-grouped view");
        return;
      }

      // Only allow for Status grouping
      if (groupBy !== "Status") {
        return;
      }

      const oldIndex = columnOrder.indexOf(activeId);
      const newIndex = columnOrder.indexOf(overId);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      // Create new column order
      const newColumnOrder = [...columnOrder];
      const [removed] = newColumnOrder.splice(oldIndex, 1);
      newColumnOrder.splice(newIndex, 0, removed);

      // Optimistically update the UI
      dispatch(setColumnOrder(newColumnOrder));

      try {
        // Update the backend
        const updatedItems = newColumnOrder.map((columnId, index) => ({
          _id: columnId,
          boardIndex: index,
        }));

        await updateStatusIndex({
          items: updatedItems,
        }).unwrap();

        toast.success(t("Column order updated successfully"));
      } catch (error: any) {
        // Revert on error
        toast.error(error?.data?.message || t("Failed to update column order"));
        dispatch(setColumnOrder(columnOrder));
      }
    },
    [columnOrder, groupBy, subGroupBy, updateStatusIndex, t, dispatch]
  );

  return {
    handleColumnDragEnd,
  };
};
